import { useCallback, useEffect, useState, useRef } from "react";
import styles from './SingleSelectSearchableDropdown.module.css';
import cx from 'classnames';

const SingleSelectSearchableDropdown = (props) => {
    const {
        fieldDisplayText = "Select",
        maxWidth = 100,
        maxWidthUnit = "px",
        SupportingIcon = null,
        listOptions = [],
        labelPosition = "BELOW",
        setSelectedOption
    } = props;

    const [selectedEntry, setSelectedEntry] = useState(null);
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [visibleOptions, setVisibleOptions] = useState(listOptions);
    const [hoveredIconId, setHoveredIconId] = useState(null);
    
    const textFieldRef = useRef();
    const dropdownRef = useRef();

    useEffect(() => {
        setVisibleOptions(listOptions);
    }, [listOptions]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsMenuVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => {
        setIsMenuVisible(!isMenuVisible);
        if (!isMenuVisible) {
            setTimeout(() => textFieldRef.current?.focus(), 0);
        }
    };

    const filterListOptions = useCallback((event) => {
        const searchValue = event?.target?.value?.toLowerCase() || "";
        if (searchValue) {
            setVisibleOptions(
                listOptions.filter(entry =>
                    entry.displayText.toLowerCase().includes(searchValue) ||
                    (entry.label && entry.label.toLowerCase().includes(searchValue))
                )
            );
        } else {
            setVisibleOptions(listOptions);
        }
    }, [listOptions]);

    const handleOptionSelect = (option) => {
        setSelectedEntry(option);
        setSelectedOption(option);
        setIsMenuVisible(false);
        if (textFieldRef.current) {
            textFieldRef.current.value = "";
        }
        setVisibleOptions(listOptions);
    };

    const handleIconHover = (optionId, isHovering) => {
        setHoveredIconId(isHovering ? optionId : null);
    };

    const maxWidthStyle = {
        maxWidth: `${maxWidth}${maxWidthUnit}`
    };

    return (
        <div 
            className={styles.dropdown_container} 
            style={maxWidthStyle}
            ref={dropdownRef}
        >
            <div className={styles.dropdown_header} onClick={toggleMenu}>
                {isMenuVisible ? (
                    <input
                        ref={textFieldRef}
                        onChange={filterListOptions}
                        className={styles.search_input}
                        type="text"
                        placeholder="Search..."
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <div className={styles.selected_value}>
                        {selectedEntry ? selectedEntry.displayText : fieldDisplayText}
                    </div>
                )}
                <div className={styles.dropdown_arrow}>
                    {isMenuVisible ? '▲' : '▼'}
                </div>
            </div>

            {isMenuVisible && (
                <div className={styles.dropdown_menu}>
                    {visibleOptions.length > 0 ? (
                        visibleOptions.map(option => (
                            <div
                                key={option.id}
                                className={cx(styles.dropdown_option, {
                                    [styles.selected]: selectedEntry?.id === option.id
                                })}
                                onClick={() => handleOptionSelect(option)}
                            >
                                <div className={cx(styles.option_content, {
                                    [styles.content_row]: labelPosition === "RIGHT",
                                    [styles.content_column]: labelPosition === "BELOW"
                                })}>
                                    <div className={styles.display_text}>
                                        {option.displayText}
                                    </div>
                                    {option.label && (
                                        <div className={styles.label_text}>
                                            {option.label}
                                        </div>
                                    )}
                                </div>
                                {SupportingIcon && (
                                    <div 
                                        className={styles.icon_container}
                                        onMouseEnter={() => handleIconHover(option.id, true)}
                                        onMouseLeave={() => handleIconHover(option.id, false)}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <SupportingIcon className={styles.supporting_icon} />
                                        {hoveredIconId === option.id && option.iconText && (
                                            <div className={styles.icon_tooltip}>
                                                {option.iconText}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className={styles.no_results}>No results found</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SingleSelectSearchableDropdown;